/**
 * The session outbox: chute results and readings saved on the device before anything says "saved",
 * sent in order with the ids the device chose, and removed only when the server returns a durable
 * receipt. Storage is an adapter (IndexedDB in the browser, memory in tests) so the rules are the
 * same everywhere and testable without a browser.
 */
import type { ReadingInput, Receipt, Submission, WorkPack } from "./types";

export interface KV {
  get<T>(store: Store, key: string): Promise<T | undefined>;
  put<T>(store: Store, key: string, value: T): Promise<void>;
  del(store: Store, key: string): Promise<void>;
  all<T>(store: Store): Promise<T[]>;
}

export type Store = "outbox" | "receipts" | "packs" | "runner";
export const STORES: Store[] = ["outbox", "receipts", "packs", "runner"];

/** In-memory storage. Used in tests and when the browser refuses to store anything. */
export class MemoryKV implements KV {
  private data = new Map<string, Map<string, unknown>>();
  private table(store: Store) {
    let t = this.data.get(store);
    if (!t) { t = new Map(); this.data.set(store, t); }
    return t;
  }
  async get<T>(store: Store, key: string) { return structuredClone(this.table(store).get(key)) as T | undefined; }
  async put<T>(store: Store, key: string, value: T) { this.table(store).set(key, structuredClone(value)); }
  async del(store: Store, key: string) { this.table(store).delete(key); }
  async all<T>(store: Store) { return [...this.table(store).values()].map((v) => structuredClone(v)) as T[]; }
}

export type QueuedState = "saved" | "sending" | "blocked" | "failed";

export interface Queued {
  client_id: string;
  owner: string;
  job_id: string;
  kind: "submission" | "reading";
  body: Submission | ReadingInput;
  state: QueuedState;
  attempts: number;
  error?: string | null;
  created_at: string;
  seq: number;
}

export interface StoredReceipt extends Receipt { owner: string; job_id: string; saved_at: string }
export interface StoredPack extends WorkPack { owner: string; job_id: string }
export interface StoredRunner { owner: string; job_id: string; saved_at: string; state: unknown }

/** What the network layer reports when a send fails. status 0 means no connection. */
export class SendError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export type Sender = (jobId: string, batch: { submissions: Submission[]; readings: ReadingInput[] }) => Promise<{ receipts: Receipt[] }>;

export interface FlushResult { sent: number; filed: number; conflicts: number; waiting: number; stopped: "offline" | "auth" | null }

const FINAL = new Set(["filed", "conflict", "discarded"]);
const key = (owner: string, id: string) => `${owner}|${id}`;

export class SessionOutbox {
  owner = "";
  private seq = 0;
  private flushing: Promise<FlushResult> | null = null;
  private listeners = new Set<() => void>();
  /** False when the last save could not be written to durable storage. */
  durable = true;

  constructor(private kv: KV, private fallback: KV = new MemoryKV(), private now: () => Date = () => new Date()) {}

  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  private emit() { this.listeners.forEach((l) => l()); }

  setOwner(owner: string): void { this.owner = owner; this.emit(); }

  private async put<T>(store: Store, k: string, value: T): Promise<boolean> {
    try { await this.kv.put(store, k, value); return true; } catch { await this.fallback.put(store, k, value); return false; }
  }
  private async allOf<T>(store: Store): Promise<T[]> {
    const [a, b] = await Promise.all([this.kv.all<T>(store).catch(() => [] as T[]), this.fallback.all<T>(store)]);
    return [...a, ...b];
  }
  private async del(store: Store, k: string): Promise<void> {
    await Promise.all([this.kv.del(store, k).catch(() => {}), this.fallback.del(store, k)]);
  }

  /** Save before anyone is told it's saved. Returns whether it reached durable storage. */
  async add(jobId: string, kind: Queued["kind"], body: Submission | ReadingInput): Promise<{ item: Queued; durable: boolean }> {
    if (!this.owner) throw new Error("No signed-in owner for the session outbox");
    const existing = (await this.list()).find((q) => q.client_id === body.client_id);
    if (existing) return { item: existing, durable: true };
    const all = await this.allOf<Queued>("outbox");
    this.seq = Math.max(this.seq, ...all.map((q) => q.seq)) + 1;
    const item: Queued = { client_id: body.client_id, owner: this.owner, job_id: jobId, kind, body, state: "saved", attempts: 0, created_at: this.now().toISOString(), seq: this.seq };
    const durable = await this.put("outbox", key(this.owner, item.client_id), item);
    this.durable = durable;
    this.emit();
    return { item, durable };
  }

  /** Waiting items for this person, oldest first. */
  async list(jobId?: string): Promise<Queued[]> {
    const all = await this.allOf<Queued>("outbox");
    const seen = new Set<string>();
    return all.filter((q) => q.owner === this.owner && (!jobId || q.job_id === jobId) && !seen.has(q.client_id) && seen.add(q.client_id)).sort((a, b) => a.seq - b.seq);
  }

  async receipts(jobId?: string): Promise<StoredReceipt[]> {
    return (await this.allOf<StoredReceipt>("receipts")).filter((r) => r.owner === this.owner && (!jobId || r.job_id === jobId));
  }

  async saveReceipt(jobId: string, receipt: Receipt): Promise<void> {
    await this.put("receipts", key(this.owner, receipt.client_id), { ...receipt, owner: this.owner, job_id: jobId, saved_at: this.now().toISOString() } satisfies StoredReceipt);
  }

  async discard(clientId: string): Promise<void> {
    await this.del("outbox", key(this.owner, clientId));
    this.emit();
  }

  /** Put a blocked or failed item back in line, for example after signing back in. */
  async retry(clientId: string): Promise<void> {
    const item = (await this.list()).find((q) => q.client_id === clientId);
    if (!item) return;
    await this.put("outbox", key(this.owner, clientId), { ...item, state: "saved", error: null });
    this.emit();
  }

  // ---------------------------------------------------------------- packs and runner state

  async savePack(pack: WorkPack): Promise<boolean> {
    const stored: StoredPack = { ...pack, owner: this.owner, job_id: pack.job.id };
    return this.put("packs", key(this.owner, pack.job.id), stored);
  }

  async pack(jobId: string): Promise<StoredPack | undefined> {
    return (await this.allOf<StoredPack>("packs")).find((p) => p.owner === this.owner && p.job_id === jobId);
  }

  async packs(): Promise<StoredPack[]> {
    return (await this.allOf<StoredPack>("packs")).filter((p) => p.owner === this.owner);
  }

  async removePack(jobId: string): Promise<void> {
    await this.del("packs", key(this.owner, jobId));
    await this.del("runner", key(this.owner, jobId));
    this.emit();
  }

  async saveRunner(jobId: string, state: unknown): Promise<void> {
    await this.put("runner", key(this.owner, jobId), { owner: this.owner, job_id: jobId, saved_at: this.now().toISOString(), state } satisfies StoredRunner);
  }

  async runner(jobId: string): Promise<StoredRunner | undefined> {
    return (await this.allOf<StoredRunner>("runner")).find((r) => r.owner === this.owner && r.job_id === jobId);
  }

  /**
   * Cached ranch data must not outlive access. When this person can no longer read the herd, drop
   * their downloaded packs and runner state; queued results stay so nothing they recorded is lost.
   */
  async purgeCaches(): Promise<number> {
    const packs = await this.packs();
    for (const p of packs) await this.removePack(p.job_id);
    return packs.length;
  }

  /**
   * On a shared phone, downloaded ranch data belongs to whoever downloaded it. When someone else
   * signs in, drop the other people's packs and drafts; their unsent results stay for them to send.
   */
  async purgeOtherOwners(): Promise<number> {
    let n = 0;
    for (const p of await this.allOf<StoredPack>("packs")) {
      if (p.owner === this.owner) continue;
      await this.del("packs", key(p.owner, p.job_id));
      await this.del("runner", key(p.owner, p.job_id));
      n++;
    }
    if (n) this.emit();
    return n;
  }

  // ---------------------------------------------------------------- sending

  /** Send everything waiting, in the order it was recorded. Safe to call often; runs one at a time. */
  flush(send: Sender, onReceipt?: (jobId: string, r: Receipt) => void): Promise<FlushResult> {
    if (!this.flushing) this.flushing = this.doFlush(send, onReceipt).finally(() => { this.flushing = null; });
    return this.flushing;
  }

  private async doFlush(send: Sender, onReceipt?: (jobId: string, r: Receipt) => void): Promise<FlushResult> {
    const result: FlushResult = { sent: 0, filed: 0, conflicts: 0, waiting: 0, stopped: null };
    const items = (await this.list()).filter((q) => q.state === "saved" || q.state === "sending");
    const jobs = [...new Set(items.map((q) => q.job_id))];
    for (const jobId of jobs) {
      const queue = items.filter((q) => q.job_id === jobId);
      for (let i = 0; i < queue.length; i += 25) {
        const batch = queue.slice(i, i + 25);
        for (const q of batch) await this.put("outbox", key(this.owner, q.client_id), { ...q, state: "sending" });
        this.emit();
        let receipts: Receipt[];
        try {
          ({ receipts } = await send(jobId, {
            submissions: batch.filter((q) => q.kind === "submission").map((q) => q.body as Submission),
            readings: batch.filter((q) => q.kind === "reading").map((q) => q.body as ReadingInput),
          }));
        } catch (err) {
          const status = err instanceof SendError ? err.status : 0;
          const message = err instanceof Error ? err.message : String(err);
          const state: QueuedState = status === 403 ? "blocked" : status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429 ? "failed" : "saved";
          for (const q of batch) await this.put("outbox", key(this.owner, q.client_id), { ...q, state, attempts: q.attempts + 1, error: message });
          this.emit();
          if (state === "saved") { result.stopped = status === 401 ? "auth" : "offline"; return result; }
          continue;
        }
        const byId = new Map(receipts.map((r) => [r.client_id, r]));
        for (const q of batch) {
          const r = byId.get(q.client_id);
          if (r && FINAL.has(r.status)) {
            await this.saveReceipt(jobId, r);
            await this.del("outbox", key(this.owner, q.client_id));
            result.sent++;
            if (r.status === "filed") result.filed++;
            if (r.status === "conflict") result.conflicts++;
            onReceipt?.(jobId, r);
          } else {
            // Waiting on something sent earlier (an animal added at the chute) or still processing.
            await this.put("outbox", key(this.owner, q.client_id), { ...q, state: "saved", attempts: q.attempts + 1, error: r?.message ?? null });
            result.waiting++;
          }
        }
        this.emit();
      }
    }
    return result;
  }
}
